import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export const getSymbols = async () => {
  try {
    const response = await api.get('/symbols', { timeout: 30000 });
    return response.data;
  } catch (error) {
    console.error('Error fetching symbols:', error);
    throw error;
  }
};

export const getExpiryDates = async (symbol, optionType) => {
  try {
    const response = await api.get('/expiry-dates', {
      params: { symbol, optionType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching expiry dates:', error);
    throw error;
  }
};

export const getMarketStatus = async () => {
  try {
    const response = await api.get('/market-status');
    return response.data;
  } catch (error) {
    console.error('Error fetching market status:', error);
    throw error;
  }
};

export const getAllIndicies = async (symbol, optionType) => {
  try {
    const response = await api.get('/allIndicies', {
      params: { symbol, optionType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all indicies data:', error);
    throw error;
  }
};

export const getOptionChainData = async (symbol, expiryDate, optionType) => {
  try {
    const response = await api.get('/option-chain', {
      params: { symbol, expiryDate, optionType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching option chain data:', error);
    throw error;
  }
};

export const getAnalysis = async (symbol, expiryDate, strikePrice, optionType) => {
  try {
    console.log('API: Fetching analysis for', { symbol, expiryDate, strikePrice, optionType });
    const response = await api.get('/analysis', {
      params: { symbol, expiryDate, strikePrice, optionType },
    });
    console.log('API: Analysis response:', response.data);
    return response.data;
  } catch (error) {
    console.error('API: Error fetching analysis:', error.message);
    console.error('Full error:', error);
    throw error;
  }
};

export const getGammaAnalysis = async (symbol, expiryDate, optionType) => {
  try {
    const response = await api.get('/gamma-analysis', {
      params: { symbol, expiryDate, optionType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching gamma analysis:', error);
    throw error;
  }
};

export const getEmaData = async (symbol) => {
  try {
    const response = await api.get('/ema', { params: { symbol } });
    return response.data;
  } catch (error) {
    console.error('Error fetching EMA data:', error);
    throw error;
  }
};

export const getFuturesData = (symbol) =>
  api.get('/futures', { params: { symbol } }).then(r => r.data);

// The backend polls at its own configured interval — the dashboard's refresh rate is browser-only.
export const triggerAutoStart = async () => {
  try {
    const response = await api.post('/auto-start', {});
    return response.data;
  } catch (error) {
    console.error('Error triggering auto-start:', error);
    throw error;
  }
};

export const getTelegramNotificationsStatus = async () => {
  const response = await api.get('/notifications/status');
  return response.data;
};

export const setTelegramNotificationsEnabled = async (enabled) => {
  const response = await api.post('/notifications/toggle', { enabled });
  return response.data;
};

export const sendTelegramNotification = async (message) => {
  try {
    const response = await api.post('/notify-telegram', { message });
    return response.data;
  } catch (error) {
    console.error('Error sending Telegram notification:', error.message || error);
    throw error;
  }
};

export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

export const getLeadingStocksCatalogue = async (symbol) => {
  try {
    const response = await api.get('/leading-stocks/catalogue', { params: { symbol } });
    return response.data;
  } catch (error) {
    console.error('Error fetching lead stocks catalogue:', error);
    return [];
  }
};

export const getLeadingStocks = async (symbol, expiryDate, selectedStocks) => {
  try {
    const params = { symbol, expiryDate };
    if (selectedStocks && selectedStocks.length > 0) params.stocks = selectedStocks.join(',');
    const response = await api.get('/leading-stocks', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching leading stocks:', error);
    return null;
  }
};

// ── Live SSE stream (replaces polling — backend pushes on every compute cycle) ─

/**
 * Opens a Server-Sent Events connection to the backend's /stream endpoint.
 * The backend pushes {symbol, analysis, gamma, ema, serverTimeIST} on every
 * Kite fast-poll tick (5 s) or NSE scheduler cycle (60 s) — no frontend polling needed.
 *
 * @param username   logged-in user's username (validated server-side)
 * @param symbol     optional — filter to one symbol; null = all symbols
 * @param onUpdate   callback(data) — called on every push event
 * @param onError    callback(err)  — called when the connection errors/closes
 * @param onTicks    optional callback(ticks) — the light 1 s "ticks" event (spot + open-strike LTPs)
 * @returns          cleanup function — call on component unmount to close stream
 */
export const subscribeToStream = (username, symbol, onUpdate, onError, onTicks) => {
  const params = new URLSearchParams({ username });
  if (symbol) params.set('symbol', symbol);
  const url = `${API_BASE_URL}/stream?${params.toString()}`;

  const es = new EventSource(url, { withCredentials: true });

  es.addEventListener('update', (e) => {
    try { onUpdate(JSON.parse(e.data)); }
    catch (err) { console.error('SSE parse error:', err); }
  });

  if (onTicks) {
    es.addEventListener('ticks', (e) => {
      try { onTicks(JSON.parse(e.data)); }
      catch (err) { console.error('SSE ticks parse error:', err); }
    });
  }

  es.onerror = (err) => {
    onError && onError(err);
    // EventSource auto-reconnects — do not call es.close() here.
  };

  return () => es.close();
};

// ── Backend autonomous snapshot (Kite fast-poll / NSE scheduler) ─────────────

/**
 * Returns the latest AnalysisData + GammaAnalysis cached by the backend's
 * autonomous Kite 5s fast-poll or NSE scheduler loop.
 * Call on page load to get an immediate state without waiting for the next interval.
 * Returns null (not throws) when no data is cached yet (first load, off-hours).
 */
export const getSnapshot = async (symbol) => {
  try {
    const response = await api.get('/snapshot', { params: { symbol } });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 204) return null; // no data yet
    console.error('Error fetching snapshot:', error);
    return null;
  }
};

// ── User Trades (server-side, cross-browser sync) ────────────────────────────

// `callerUsername` is always the real logged-in identity. `owner`/`symbol` (optional) select
// whose trades to fetch — omit for self; owner: 'SYSTEM' requires symbol (server-enforced);
// any other owner requires the caller to be ADMIN (server-enforced).
export const fetchUserTrades = (callerUsername, { owner, symbol } = {}) =>
  api.get('/user-trades', {
    headers: { 'X-Username': callerUsername },
    params: { ...(owner ? { owner } : {}), ...(symbol ? { symbol } : {}) },
  }).then(r => r.data);

export const saveUserTrade = (username, trade) =>
  api.post('/user-trades', trade, { headers: { 'X-Username': username } }).then(r => r.data);

export const patchUserTrade = (username, clientId, patch) =>
  api.patch(`/user-trades/${clientId}`, patch, { headers: { 'X-Username': username } }).then(r => r.data);

export const clearUserTrades = (username, symbol) =>
  api.delete('/user-trades', { headers: { 'X-Username': username }, params: { symbol } });

// ── Auth ─────────────────────────────────────────────────────────────────────

export const checkSetupRequired = async () => {
  const response = await api.get('/auth/setup-required');
  return response.data;
};

export const setupAdmin = async (username, password) => {
  const response = await api.post('/auth/setup', { username, password });
  return response.data;
};

export const loginUser = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const listUsers = async (requester) => {
  const response = await api.get('/auth/users', { headers: { 'X-Username': requester } });
  return response.data;
};

export const createUser = async (requester, username, password, role, access, telegramChatId = null) => {
  const body = { username, password, role, access };
  if (telegramChatId) body.telegramChatId = telegramChatId;
  const response = await api.post('/auth/users', body,
    { headers: { 'X-Username': requester } });
  return response.data;
};

export const updateUserAccess = async (requester, username, access) => {
  const response = await api.patch(`/auth/users/${username}/access`, { access },
    { headers: { 'X-Username': requester } });
  return response.data;
};

export const updateUserLotConfig = async (requester, username, onRiskLots, takeTradeLots) => {
  const response = await api.patch(`/auth/users/${username}/lot-config`, { onRiskLots, takeTradeLots },
    { headers: { 'X-Username': requester } });
  return response.data;
};

// accountCapitalRs: null/omitted disables capital-aware sizing (falls back to fixed lot
// counts) — that's the intentional way to turn it off, not an error.
export const updateUserCapitalConfig = async (requester, username, accountCapitalRs, onRiskRiskPct, takeTradeRiskPct) => {
  const response = await api.patch(`/auth/users/${username}/capital-config`,
    { accountCapitalRs, onRiskRiskPct, takeTradeRiskPct },
    { headers: { 'X-Username': requester } });
  return response.data;
};

// Admin-only: switch the SYSTEM engine's range-day option selling on/off (null = server default).
export const updateUserSellerConfig = async (requester, username, sellerEnabled) => {
  const response = await api.patch(`/auth/users/${username}/seller-config`,
    { sellerEnabled },
    { headers: { 'X-Username': requester } });
  return response.data;
};

// Admin-only: override the SYSTEM daily limits. Each value null = back to the server default
// (limits.* property), 0 = that limit off.
// Admin-only: pause SYSTEM entries when the data isn't from Kite ticks (no Kite login today).
export const updateUserFeedConfig = async (requester, username, pauseWithoutKite) => {
  const response = await api.patch(`/auth/users/${username}/feed-config`,
    { pauseWithoutKite },
    { headers: { 'X-Username': requester } });
  return response.data;
};

export const updateUserLimitsConfig = async (requester, username, limits) => {
  const response = await api.patch(`/auth/users/${username}/limits-config`,
    limits,
    { headers: { 'X-Username': requester } });
  return response.data;
};

export const deleteUser = async (requester, username) => {
  const response = await api.delete(`/auth/users/${username}`,
    { headers: { 'X-Username': requester } });
  return response.data;
};

// ── Admin Trade Dashboard ─────────────────────────────────────────────────────

// ── Performance tab ──────────────────────────────────────────────────────────
// owner: 'SYSTEM' (admin only) or omit for your own book. from/to: yyyy-MM-dd, optional.
export const getPerformance = (username, { owner, from, to } = {}) =>
  api.get('/performance', {
    headers: { 'X-Username': username },
    params: { ...(owner ? { owner } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) },
    timeout: 30000,
  }).then(r => r.data);

// entry: { kind: 'OPENING'|'DEPOSIT'|'WITHDRAWAL', date: 'yyyy-MM-dd', amountRs, note }
export const addLedgerEntry = (username, owner, entry) =>
  api.post('/performance/ledger', entry, {
    headers: { 'X-Username': username },
    params: owner ? { owner } : {},
  }).then(r => r.data);

export const deleteLedgerEntry = (username, id) =>
  api.delete(`/performance/ledger/${id}`, { headers: { 'X-Username': username } });

export const fetchAllUserTrades = (adminUsername) =>
  api.get('/user-trades/admin/all', { headers: { 'X-Username': adminUsername } })
     .then(r => r.data);

export const getDayEndReport = (adminUsername, date) =>
  api.get('/user-trades/admin/day-report', {
    headers: { 'X-Username': adminUsername },
    params: date ? { date } : {},
  }).then(r => r.data);

export const sendDayReportToTelegram = (adminUsername, date) =>
  api.post('/user-trades/admin/send-day-report',
    date ? { date } : {},
    { headers: { 'X-Username': adminUsername } }
  ).then(r => r.data);

// ── Kite Connect (Zerodha) ────────────────────────────────────────────────────
// The entire Kite integration is admin-only — every endpoint now requires an X-Username
// header identifying an ADMIN account (backend-enforced in KiteController, not just hidden
// behind frontend routing). All these functions therefore take the caller's own username.

export const getKiteLoginUrl = (username) =>
  api.get('/kite/login-url', { headers: { 'X-Username': username } }).then(r => r.data.loginUrl);

export const exchangeKiteToken = (requestToken, username) =>
  api.post(`/kite/session?requestToken=${encodeURIComponent(requestToken)}`, null,
    { headers: { 'X-Username': username } }).then(r => r.data);

const kiteHeaders = (accessToken, username) =>
  ({ headers: { 'X-Kite-Token': accessToken, 'X-Username': username } });

export const getKiteProfile   = (token, username) => api.get('/kite/profile',   kiteHeaders(token, username)).then(r => r.data);
export const getKiteMargins   = (token, username) => api.get('/kite/margins',   kiteHeaders(token, username)).then(r => r.data);
export const getKiteHoldings  = (token, username) => api.get('/kite/holdings',  kiteHeaders(token, username)).then(r => r.data);
export const getKitePositions = (token, username) => api.get('/kite/positions', kiteHeaders(token, username)).then(r => r.data);
export const getKiteOrders    = (token, username) => api.get('/kite/orders',    kiteHeaders(token, username)).then(r => r.data);
export const logoutKite       = (token, username) => api.delete('/kite/session', kiteHeaders(token, username)).then(r => r.data);

export const getKiteExpiries  = (symbol, token, username) =>
  api.get('/kite/instruments/expiries', { params: { symbol },
    headers: { 'X-Kite-Token': token, 'X-Username': username } }).then(r => r.data);

// entries: [{ symbol, expiry }, ...] — subscribes all of them on one shared WS connection
export const startKiteStream  = (entries, token, username) =>
  api.post('/kite/stream/start', entries, kiteHeaders(token, username)).then(r => r.data);

export const stopKiteStream   = (username) =>
  api.delete('/kite/stream/stop', { headers: { 'X-Username': username } }).then(r => r.data);

export const getKiteStreamStatus = (username) =>
  api.get('/kite/stream/status', { headers: { 'X-Username': username } }).then(r => r.data);

export const getKiteAnalysis = (symbol, expiry, strikePrice = 0, username) =>
  api.get('/kite/analysis', { params: { symbol, expiry, strikePrice },
    headers: { 'X-Username': username } }).then(r => r.data);

export const getKiteGammaAnalysis = (symbol, expiry, username) =>
  api.get('/kite/gamma-analysis', { params: { symbol, expiry },
    headers: { 'X-Username': username } }).then(r => r.data);

// Backend truth for a symbol's autonomous SYSTEM engine — circuit breaker state, today's
// win/loss count, and the currently open position (if any). Used to mirror the admin's own
// dashboard onto the backend instead of running an independent, out-of-sync local copy.
export const getSystemStatus = (symbol, username) =>
  api.get('/kite/system-status', { params: { symbol }, headers: { 'X-Username': username } }).then(r => r.data);

// Weekly paper condor (WeeklySeller): rules, the open condor with its latest mark, closed condors.
export const getWeeklySeller = (username) =>
  api.get('/kite/weekly-seller', { headers: { 'X-Username': username } }).then(r => r.data);

// Level short (LevelPutSpread): paper put debit spreads at rejected day/week levels — rules, open, history.
export const getLevelShorts = (username) =>
  api.get('/kite/level-shorts', { headers: { 'X-Username': username } }).then(r => r.data);

export default api;
